import { SENHA_MINIMO, problemaSenha, type CanalRecuperacao, type Eu } from "@horofacial/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, LoaderCircle, LogOut, Mail, MessageCircle } from "lucide-react";
import { createContext, useContext, useState, type FormEvent, type ReactNode } from "react";
import { Configurar2fa } from "../componentes/Configurar2fa";
import { useProduto } from "../lib/produto";
import { VERSAO } from "../lib/versao";
import { ErroRequisicao } from "../lib/api";
import { CHAVE_EU, useConfirmar2fa, useConfirmarRecuperacao, useEntrar, useRecuperarSenha, useSair, useTrocarSenha } from "../lib/sessao";

// Entrada por cima da tela em uso, quando a sessão termina no meio do trabalho.
const ContextoSobreposta = createContext(false);

/** Entrada (ou etapa pendente) sobre a tela atual, sem desmontá-la: o que estava digitado continua lá. */
export function EntradaSobreposta({ eu }: { eu: Eu | null }) {
  return <ContextoSobreposta.Provider value={true}>{eu ? <PaginaEtapa eu={eu} /> : <PaginaEntrar />}</ContextoSobreposta.Provider>;
}

/**
 * Moldura das telas de entrada: tela cheia. No computador, painel com o nome do produto à esquerda e o formulário
 * à direita; no celular, só o formulário. Por cima da tela em uso (sessão encerrada), vira um cartão no centro.
 */
export function Moldura({ titulo, subtitulo, children, rodape, nota }: { titulo?: string; subtitulo: string; children: ReactNode; rodape?: ReactNode; nota?: ReactNode }) {
  const sobreposta = useContext(ContextoSobreposta);
  // Antes do login a tela é a mesma para todas as clínicas: só o nome do produto.
  const produto = useProduto();
  const formulario = (
    <div className="w-full max-w-[400px] [&_.botao-primario]:min-h-12 [&_.botao-primario]:text-[16px] [&_label]:text-[13px] [&_label]:font-medium">
      <div className="mb-8">
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <img src="/icone.svg" alt="" className="size-10 rounded-[var(--raio)]" />
          <span className="min-h-7 text-[20px] font-semibold tracking-[-0.02em]">{produto}</span>
        </div>
        <h1 className="text-[28px] leading-tight font-semibold">{titulo ?? "Entrar"}</h1>
        <p className="mt-2 text-[15px] text-texto-suave">{subtitulo}</p>
      </div>
      {sobreposta && (
        <p className="mb-4 rounded-[var(--raio)] bg-aviso-suave px-3 py-2 text-sm text-aviso">Sua sessão terminou. Entre de novo para continuar: o que estava na tela foi mantido.</p>
      )}
      {children}
      {nota && <p className="mt-5 text-[13px] text-texto-suave">{nota}</p>}
      {rodape && <div className="mt-5">{rodape}</div>}
      {!sobreposta && <p className="mt-10 text-[12px] text-texto-fraco tabular-nums">v{VERSAO}</p>}
    </div>
  );
  if (sobreposta) {
    return (
      <div role="dialog" aria-modal="true" aria-label="Sessão encerrada" className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-black/50 px-4 py-6 backdrop-blur-sm">
        <section className="cartao sobreposta w-full max-w-[460px] p-6 sm:p-8">{formulario}</section>
      </div>
    );
  }
  return (
    <main className="grid min-h-dvh lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      {/* Painel da marca do produto: gradiente da cor de destaque, sem dado de clínica. */}
      <aside
        aria-hidden
        className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16"
        style={{ background: "radial-gradient(120% 90% at 0% 0%, color-mix(in oklab, var(--marca) 55%, #0b0b0c) 0%, #0b0b0c 70%)" }}
      >
        <div className="flex items-center gap-3">
          <img src="/icone.svg" alt="" className="size-11 rounded-[var(--raio)]" />
          <span className="text-[22px] font-semibold tracking-[-0.02em] text-white">{produto}</span>
        </div>
        <div className="max-w-xl">
          <p className="text-[44px] leading-[1.08] font-semibold tracking-[-0.035em] text-white xl:text-[56px]">Agenda, prontuário, financeiro e WhatsApp da clínica.</p>
          <p className="mt-5 text-[17px] text-white/70">Em um só lugar, no computador, no tablet e no celular.</p>
        </div>
        <div className="pointer-events-none absolute -right-40 -bottom-40 size-[34rem] rounded-full opacity-40 blur-3xl" style={{ background: "var(--marca)" }} />
      </aside>
      <div className="flex items-center justify-center px-6 py-[calc(2.5rem+env(safe-area-inset-top))] sm:px-10">{formulario}</div>
    </main>
  );
}

function mensagem(e: unknown) {
  return e instanceof ErroRequisicao ? e.corpo.mensagem : "Falha na comunicação. Tente de novo.";
}

/** Senha com o botão de mostrar e ocultar. */
function CampoSenha({ id, valor, aoMudar, autoComplete, invalido }: { id: string; valor: string; aoMudar: (v: string) => void; autoComplete: string; invalido?: boolean }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <div className="relative">
      <input id={id} type={visivel ? "text" : "password"} className="campo pr-12" autoComplete={autoComplete} required value={valor} onChange={(e) => aoMudar(e.target.value)} aria-invalid={invalido || undefined} />
      <button
        type="button"
        className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-[var(--raio)] text-texto-suave hover:text-texto"
        aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
        aria-pressed={visivel}
        onClick={() => setVisivel(!visivel)}
      >
        {visivel ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
      </button>
    </div>
  );
}

const ErroAlerta = ({ erro }: { erro: unknown }) => (
  <p role="alert" className="rounded-xl bg-perigo-suave px-3 py-2 text-sm text-perigo">
    {mensagem(erro)}
  </p>
);

const LINK = "block min-h-9 w-full text-center text-[15px] font-semibold text-marca max-sm:min-h-11";
const NOTA_ACESSO = "Acesso criado pelo administrador da clínica.";

/** Entrada, "esqueci minha senha" e a criação da nova senha com o código recebido pelo WhatsApp. */
export function PaginaEntrar() {
  const [modo, setModo] = useState<"entrar" | "recuperar" | "codigo">("entrar");
  const [login, setLogin] = useState("");
  const [canal, setCanal] = useState<CanalRecuperacao>("whatsapp");
  const [aviso, setAviso] = useState<string | null>(null);
  if (modo === "recuperar") {
    return <Recuperar login={login} setLogin={setLogin} canal={canal} setCanal={setCanal} aoEnviar={() => setModo("codigo")} aoVoltar={() => setModo("entrar")} />;
  }
  if (modo === "codigo") {
    return (
      <NovaSenha
        login={login}
        canal={canal}
        aoConcluir={() => {
          setAviso("Senha alterada. Entre com a nova senha.");
          setModo("entrar");
        }}
        aoVoltar={() => setModo("entrar")}
      />
    );
  }
  return (
    <Entrar
      login={login}
      setLogin={setLogin}
      aviso={aviso}
      aoEsquecer={() => {
        setAviso(null);
        setModo("recuperar");
      }}
    />
  );
}

function Entrar({ login, setLogin, aviso, aoEsquecer }: { login: string; setLogin: (v: string) => void; aviso: string | null; aoEsquecer: () => void }) {
  const entrar = useEntrar();
  const [senha, setSenha] = useState("");
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    entrar.mutate({ login, senha });
  };
  return (
    <Moldura subtitulo="Entre para gerenciar sua clínica." nota={NOTA_ACESSO}>
      <form onSubmit={enviar} className="space-y-4">
        {aviso && (
          <p role="status" className="rounded-xl bg-ok-suave px-3 py-2 text-sm text-ok">
            {aviso}
          </p>
        )}
        <div>
          <label htmlFor="login" className="mb-1.5 block">
            E-mail ou celular
          </label>
          <input id="login" className="campo" autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus required value={login} onChange={(e) => setLogin(e.target.value)} />
        </div>
        <div>
          <label htmlFor="senha" className="mb-1.5 block">
            Senha
          </label>
          <CampoSenha id="senha" valor={senha} aoMudar={setSenha} autoComplete="current-password" />
        </div>
        {entrar.isError && <ErroAlerta erro={entrar.error} />}
        <div className="space-y-3 pt-1">
          <button type="submit" className="botao botao-primario w-full" disabled={entrar.isPending}>
            {entrar.isPending && <LoaderCircle className="size-4 animate-spin" />}
            Entrar
          </button>
          <button type="button" className={LINK} onClick={aoEsquecer}>
            Esqueci minha senha
          </button>
        </div>
      </form>
    </Moldura>
  );
}

const CANAIS: { canal: CanalRecuperacao; rotulo: string; icone: ReactNode }[] = [
  { canal: "whatsapp", rotulo: "WhatsApp", icone: <MessageCircle className="size-[18px]" /> },
  { canal: "email", rotulo: "E-mail", icone: <Mail className="size-[18px]" /> },
];

function Recuperar({
  login,
  setLogin,
  canal,
  setCanal,
  aoEnviar,
  aoVoltar,
}: {
  login: string;
  setLogin: (v: string) => void;
  canal: CanalRecuperacao;
  setCanal: (c: CanalRecuperacao) => void;
  aoEnviar: () => void;
  aoVoltar: () => void;
}) {
  const recuperar = useRecuperarSenha();
  return (
    <Moldura titulo="Esqueci minha senha" subtitulo="Informe o e-mail ou o celular cadastrado e escolha onde receber o código." nota={NOTA_ACESSO}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          recuperar.mutate({ login, canal }, { onSuccess: aoEnviar });
        }}
        className="space-y-4"
      >
        <div>
          <label htmlFor="login-recuperar" className="mb-1.5 block">
            E-mail ou celular
          </label>
          <input id="login-recuperar" className="campo" autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus required value={login} onChange={(e) => setLogin(e.target.value)} />
        </div>
        <fieldset>
          <legend className="mb-1.5 block text-[13px] font-semibold">Receber o código por</legend>
          <div className="grid grid-cols-2 gap-2">
            {CANAIS.map((c) => (
              <label
                key={c.canal}
                className={`flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[var(--raio)] border text-[15px] font-medium has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-marca ${
                  canal === c.canal ? "border-marca bg-marca-suave text-marca" : "border-[var(--borda-campo)] bg-preenchimento-3"
                }`}
              >
                <input type="radio" name="canal-recuperacao" value={c.canal} checked={canal === c.canal} onChange={() => setCanal(c.canal)} className="sr-only" />
                {c.icone}
                {c.rotulo}
              </label>
            ))}
          </div>
        </fieldset>
        {recuperar.isError && <ErroAlerta erro={recuperar.error} />}
        <div className="space-y-3 pt-1">
          <button type="submit" className="botao botao-primario w-full" disabled={recuperar.isPending || login.trim().length < 3}>
            {recuperar.isPending && <LoaderCircle className="size-4 animate-spin" />}
            Enviar código
          </button>
          <button type="button" className={LINK} onClick={aoVoltar}>
            Voltar para a entrada
          </button>
        </div>
      </form>
    </Moldura>
  );
}

function NovaSenha({ login, canal, aoConcluir, aoVoltar }: { login: string; canal: CanalRecuperacao; aoConcluir: () => void; aoVoltar: () => void }) {
  const confirmar = useConfirmarRecuperacao();
  const reenviar = useRecuperarSenha();
  const [codigo, setCodigo] = useState("");
  const [nova, setNova] = useState("");
  const [repetir, setRepetir] = useState("");
  const [codigo2fa, setCodigo2fa] = useState("");
  // O campo do autenticador só aparece quando o servidor pede (conta com 2FA).
  const [pede2fa, setPede2fa] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const problema = nova ? problemaSenha(nova, [login]) : null;
  const diferentes = repetir.length > 0 && repetir !== nova;
  const campos = confirmar.error instanceof ErroRequisicao ? (confirmar.error.corpo.campos ?? {}) : {};

  return (
    <Moldura
      titulo="Crie uma nova senha"
      subtitulo={`Se o e-mail ou o celular estiver cadastrado, um código de 6 números chegou ${canal === "email" ? "no seu e-mail" : "pelo WhatsApp"}.`}
      nota="Não recebeu? Volte e tente pelo outro canal, ou peça ao administrador da clínica uma senha temporária."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirmar.mutate(
            { login, codigo, nova, ...(pede2fa ? { codigo2fa } : {}) },
            {
              onSuccess: aoConcluir,
              onError: (erro) => {
                if (erro instanceof ErroRequisicao && (erro.corpo.erro === "2fa" || erro.corpo.erro === "codigo2fa")) setPede2fa(true);
              },
            },
          );
        }}
        className="space-y-4"
      >
        <div>
          <label htmlFor="codigo-recuperacao" className="mb-1.5 block">
            {canal === "email" ? "Código do e-mail" : "Código do WhatsApp"}
          </label>
          <input
            id="codigo-recuperacao"
            className="campo text-center font-mono text-xl tracking-[0.3em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder="000000"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
            aria-invalid={Boolean(campos.codigo) || undefined}
          />
        </div>
        <div>
          <label htmlFor="nova-senha" className="mb-1.5 block">
            Nova senha
          </label>
          <CampoSenha id="nova-senha" valor={nova} aoMudar={setNova} autoComplete="new-password" invalido={Boolean(problema)} />
          <p className={`mt-1 text-[13px] ${problema ? "text-perigo" : "text-texto-suave"}`}>
            {problema ?? `Pelo menos ${SENHA_MINIMO} caracteres. Uma frase curta é fácil de lembrar e difícil de adivinhar.`}
          </p>
        </div>
        <div>
          <label htmlFor="nova-senha-repetir" className="mb-1.5 block">
            Repita a nova senha
          </label>
          <CampoSenha id="nova-senha-repetir" valor={repetir} aoMudar={setRepetir} autoComplete="new-password" invalido={diferentes} />
          {diferentes && <p className="mt-1 text-[13px] text-perigo">As senhas não conferem</p>}
        </div>
        {pede2fa && (
          <div>
            <label htmlFor="codigo-2fa-recuperacao" className="mb-1.5 block">
              Código do aplicativo autenticador
            </label>
            <input
              id="codigo-2fa-recuperacao"
              className="campo text-center font-mono text-xl tracking-[0.3em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={9}
              placeholder="000000"
              value={codigo2fa}
              onChange={(e) => setCodigo2fa(e.target.value)}
            />
            <p className="mt-1 text-[13px] text-texto-suave">Sua conta usa verificação em duas etapas. Vale também um código de recuperação.</p>
          </div>
        )}
        {confirmar.isError && <ErroAlerta erro={confirmar.error} />}
        {reenviado && (
          <p role="status" className="rounded-xl bg-ok-suave px-3 py-2 text-sm text-ok">
            Se o cadastro permitir, um novo código foi enviado. O anterior deixou de valer.
          </p>
        )}
        <div className="space-y-3 pt-1">
          <button type="submit" className="botao botao-primario w-full" disabled={confirmar.isPending || codigo.length !== 6 || !nova || Boolean(problema) || repetir !== nova || (pede2fa && !codigo2fa)}>
            {confirmar.isPending && <LoaderCircle className="size-4 animate-spin" />}
            Salvar nova senha
          </button>
          <button type="button" className={LINK} disabled={reenviar.isPending} onClick={() => reenviar.mutate({ login, canal }, { onSuccess: () => setReenviado(true) })}>
            Reenviar código
          </button>
          <button type="button" className={LINK} onClick={aoVoltar}>
            Voltar para a entrada
          </button>
        </div>
      </form>
    </Moldura>
  );
}

/** Telas intermediárias do login: segundo fator, troca obrigatória de senha, configuração de 2FA. */
export function PaginaEtapa({ eu }: { eu: Eu }) {
  const sair = useSair();
  const rodape = (
    <button type="button" className="inline-flex items-center gap-2 text-sm text-texto-suave hover:text-texto" onClick={() => sair.mutate()}>
      <LogOut className="size-4" /> Sair ({eu.usuario.email})
    </button>
  );
  if (eu.etapa === "2fa") return <Etapa2fa rodape={rodape} />;
  if (eu.etapa === "trocar_senha") return <EtapaTrocarSenha eu={eu} rodape={rodape} />;
  return <EtapaConfigurar2fa rodape={rodape} />;
}

function Etapa2fa({ rodape }: { rodape: ReactNode }) {
  const confirmar = useConfirmar2fa();
  const [recuperacao, setRecuperacao] = useState(false);
  const [codigo, setCodigo] = useState("");
  return (
    <Moldura titulo="Verificação em duas etapas" subtitulo={recuperacao ? "Digite um dos códigos de recuperação." : "Digite o código do aplicativo autenticador."} rodape={rodape}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirmar.mutate({ codigo });
        }}
        className="space-y-4"
      >
        <input
          aria-label={recuperacao ? "Código de recuperação" : "Código de 6 dígitos"}
          className="campo text-center font-mono text-xl tracking-[0.3em]"
          inputMode={recuperacao ? "text" : "numeric"}
          autoComplete="one-time-code"
          autoFocus
          maxLength={recuperacao ? 9 : 6}
          placeholder={recuperacao ? "xxxx-xxxx" : "000000"}
          value={codigo}
          onChange={(e) => setCodigo(recuperacao ? e.target.value : e.target.value.replace(/\D/g, ""))}
        />
        {confirmar.isError && (
          <p role="alert" className="rounded-xl bg-perigo-suave px-3 py-2 text-sm text-perigo">
            {mensagem(confirmar.error)}
          </p>
        )}
        <button type="submit" className="botao botao-primario w-full" disabled={confirmar.isPending || codigo.length < 6}>
          {confirmar.isPending && <LoaderCircle className="size-4 animate-spin" />}
          Confirmar
        </button>
        <button
          type="button"
          className={LINK}
          onClick={() => {
            setRecuperacao(!recuperacao);
            setCodigo("");
          }}
        >
          {recuperacao ? "Usar o aplicativo" : "Perdi o celular: usar código de recuperação"}
        </button>
      </form>
    </Moldura>
  );
}

function EtapaTrocarSenha({ eu, rodape }: { eu: Eu; rodape: ReactNode }) {
  return (
    <Moldura titulo="Crie sua senha" subtitulo="A senha atual é temporária. Escolha uma senha só sua." rodape={rodape}>
      <FormTrocarSenha eu={eu} rotuloAtual="Senha temporária" />
    </Moldura>
  );
}

export function FormTrocarSenha({ eu, rotuloAtual = "Senha atual", aoConcluir }: { eu: Eu; rotuloAtual?: string; aoConcluir?: () => void }) {
  const trocar = useTrocarSenha();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [repetir, setRepetir] = useState("");
  const problema = nova ? problemaSenha(nova, [eu.usuario.nome, eu.usuario.email]) : null;
  const diferentes = repetir.length > 0 && repetir !== nova;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        trocar.mutate({ atual, nova }, { onSuccess: () => aoConcluir?.() });
      }}
      className="space-y-4"
    >
      <div>
        <label htmlFor="senha-atual" className="mb-1.5 block text-sm font-medium">
          {rotuloAtual}
        </label>
        <input id="senha-atual" type="password" className="campo" autoComplete="current-password" value={atual} onChange={(e) => setAtual(e.target.value)} />
      </div>
      <div>
        <label htmlFor="senha-nova" className="mb-1.5 block text-sm font-medium">
          Nova senha
        </label>
        <input id="senha-nova" type="password" className="campo" autoComplete="new-password" value={nova} onChange={(e) => setNova(e.target.value)} aria-invalid={Boolean(problema)} />
        <p className={`mt-1 text-[13px] ${problema ? "text-perigo" : "text-texto-suave"}`}>
          {problema ?? `Pelo menos ${SENHA_MINIMO} caracteres. Uma frase curta é fácil de lembrar e difícil de adivinhar.`}
        </p>
      </div>
      <div>
        <label htmlFor="senha-repetir" className="mb-1.5 block text-sm font-medium">
          Repita a nova senha
        </label>
        <input id="senha-repetir" type="password" className="campo" autoComplete="new-password" value={repetir} onChange={(e) => setRepetir(e.target.value)} aria-invalid={diferentes} />
        {diferentes && <p className="mt-1 text-[13px] text-perigo">As senhas não conferem</p>}
      </div>
      {trocar.isError && (
        <p role="alert" className="rounded-xl bg-perigo-suave px-3 py-2 text-sm text-perigo">
          {mensagem(trocar.error)}
        </p>
      )}
      <button type="submit" className="botao botao-primario w-full" disabled={trocar.isPending || !atual || !nova || Boolean(problema) || repetir !== nova}>
        {trocar.isPending && <LoaderCircle className="size-4 animate-spin" />}
        Salvar senha
      </button>
    </form>
  );
}

function EtapaConfigurar2fa({ rodape }: { rodape: ReactNode }) {
  const cliente = useQueryClient();
  return (
    <Moldura titulo="Ative a verificação em duas etapas" subtitulo="Obrigatória para o seu perfil nesta clínica." rodape={rodape}>
      <Configurar2fa aoConcluir={() => cliente.invalidateQueries({ queryKey: CHAVE_EU })} />
    </Moldura>
  );
}
